import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, X } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import type { BillingActionNeeded } from './billing-dashboard.types';
import { dismissActionsNeededStrip, isActionsNeededDismissed } from './billing-actions-dismiss-storage';

/** Collapsed: single alert; expanded: up to this many. */
const COLLAPSED_COUNT = 1;
const EXPANDED_MAX = 5;

/** Cancels AppLayout main padding so the strip runs edge-to-edge within the content column. */
const BLEED =
  '-mx-4 -mt-4 w-[calc(100%+2rem)] md:-mx-6 md:-mt-6 md:w-[calc(100%+3rem)] lg:-mx-8 lg:-mt-8 lg:w-[calc(100%+4rem)]';

type Props = {
  items: BillingActionNeeded[];
};

const BillingActionsNeededSection: React.FC<Props> = ({ items }) => {
  const [expanded, setExpanded] = useState(false);
  const [hiddenByDismiss, setHiddenByDismiss] = useState(() =>
    typeof window !== 'undefined' && isActionsNeededDismissed()
  );

  const maxVisible = expanded ? EXPANDED_MAX : COLLAPSED_COUNT;
  const visible = useMemo(
    () => items.slice(0, maxVisible),
    [items, maxVisible]
  );

  const canExpand = items.length > COLLAPSED_COUNT;

  const handleDismissStrip = () => {
    dismissActionsNeededStrip();
    setHiddenByDismiss(true);
  };

  if (hiddenByDismiss) {
    return null;
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <section
      className={`relative z-10 shrink-0 border-b border-red-200/90 bg-[#fdf6f6] ${BLEED}`}
      aria-labelledby="billing-actions-needed-heading"
    >
      <div className="flex items-start justify-between gap-2 px-3 py-2 md:px-4 md:py-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2
              id="billing-actions-needed-heading"
              className="text-sm font-semibold text-red-950"
            >
              Actions needed
            </h2>
            <span
              className="inline-flex min-h-5 min-w-5 items-center justify-center rounded-md bg-red-200/90 px-1.5 text-[11px] font-semibold text-red-950"
              title="Total alerts still outstanding"
            >
              {items.length}
            </span>
            {canExpand && (
              <button
                type="button"
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-red-800/90 transition-colors hover:bg-red-200/60 hover:text-red-950"
                aria-expanded={expanded}
                aria-controls="billing-actions-needed-list"
                title={expanded ? 'Show one at a time' : `Show up to ${EXPANDED_MAX} alerts`}
                onClick={() => setExpanded((e) => !e)}
              >
                <ChevronDown
                  className={`size-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
                  aria-hidden
                />
                <span className="sr-only">
                  {expanded ? 'Collapse list' : 'Expand list'}
                </span>
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-red-800/80 transition-colors hover:bg-red-200/70 hover:text-red-950"
          title="Dismiss for up to 3 days"
          aria-label="Dismiss actions needed for up to 3 days"
          onClick={handleDismissStrip}
        >
          <X
            className="size-4"
            strokeWidth={2}
          />
        </button>
      </div>
      <ul
        id="billing-actions-needed-list"
        className="space-y-1.5 px-3 pb-2 pt-1.5 md:px-4"
      >
        {visible.map((item) => (
          <li
            key={item.id}
            className="flex flex-col gap-1.5 rounded-lg border border-red-200/70 bg-white/95 p-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
          >
            <div className="min-w-0 flex-1">
              <p className="text-xs leading-snug text-red-950/90">
                {item.description}
              </p>
              <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs font-medium text-red-950">
                <span className="truncate">
                  {item.affectedRecord}
                </span>
                {item.recordCount != null && item.recordCount > 1 && (
                  <span className="inline-flex shrink-0 items-center rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-900">
                    ×
                    {item.recordCount}
                  </span>
                )}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:justify-end">
              <Button
                asChild
                variant="outline"
                size="sm"
                className="h-7 border-red-300/80 bg-white px-2 text-xs text-red-950 hover:bg-red-50"
              >
                <Link to={item.resolveHref}>
                  {item.resolveLabel}
                </Link>
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default BillingActionsNeededSection;
